import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/useAuthStore';
import useInboxStore from '../store/useInboxStore';
import useToastStore from '../store/useToastStore';
import { playOrderSound, playMessageSound } from '../utils/sounds';
import { initSocket, disconnectSocket } from '../services/socket';
import api from '../services/api';

// Polls for new BOOKING_NEW notifications for providers and plays cash register sound.
// Also listens globally for new messages and shows toast notifications.
export default function NotificationListener() {
  const { user, isAuthenticated, accessToken } = useAuthStore();
  const { incrementUnread } = useInboxStore();
  const lastIdRef = useRef(null);
  const firstRunRef = useRef(true);
  const navigate = useNavigate();
  // Use ref so socket handler always reads current chatOpen without re-subscribing
  const chatOpenRef = useRef(false);
  useEffect(() => {
    return useInboxStore.subscribe(state => { chatOpenRef.current = state.chatOpen; });
  }, []);

  // Global socket: init once authenticated, disconnect on logout
  useEffect(() => {
    if (!isAuthenticated || !accessToken) return;
    const socket = initSocket(accessToken);

    socket.on('new_message', (msg) => {
      const isFromOther = msg.senderId !== user?.id;
      if (!isFromOther) return;

      // Only notify if not currently in an active chat
      if (!chatOpenRef.current) {
        const senderName = msg.sender?.fullName || 'Someone';
        const preview = msg.content?.slice(0, 45) || '';
        const previewText = preview.length < (msg.content?.length || 0) ? preview + '…' : preview;

        playMessageSound();
        incrementUnread();

        useToastStore.getState().addToast(
          `💬 ${senderName}: ${previewText}`,
          'message',
          5000,
          () => navigate('/inbox')
        );
      }
    });

    return () => {
      socket.off('new_message');
    };
  }, [isAuthenticated, accessToken, user?.id]);

  // Disconnect when logged out
  useEffect(() => {
    if (!isAuthenticated) disconnectSocket();
  }, [isAuthenticated]);

  // Poll for provider order notifications
  useEffect(() => {
    if (!isAuthenticated || user?.accountType !== 'PROVIDER') return;

    const check = async () => {
      try {
        const { data } = await api.get('/notifications');
        const notifs = data || [];
        const orderNotifs = notifs.filter(n => n.type === 'BOOKING_NEW');

        if (firstRunRef.current) {
          lastIdRef.current = orderNotifs[0]?.id || null;
          firstRunRef.current = false;
          return;
        }

        const newestId = orderNotifs[0]?.id || null;
        if (newestId && newestId !== lastIdRef.current) {
          playOrderSound();
          lastIdRef.current = newestId;
        }
      } catch {}
    };

    check();
    const interval = setInterval(check, 20000);
    return () => clearInterval(interval);
  }, [isAuthenticated, user?.accountType]);

  return null;
}
